import { useState } from "react";
import { Button, Field, DialogCancel } from "@cntrl/ui";
import { useAddMilestone, useUpdateMilestone, type MilestoneUpsertRequest } from "../../app/hooks/useRelease.js";
import type { components } from "@cntrl/api-client";
import { Modal } from "./Modal.js";
import styles from "./MilestoneModal.module.scss";

type MilestoneDto = components["schemas"]["MilestoneDto"];

// The timeline milestone editor (shot-15) — add or edit a dated, named milestone (+ optional note).
// Per-row CRUD (no If-Match). Server-confirm THEN close (never a phantom success).

export interface MilestoneModalProps {
  releaseId: string;
  /** An existing milestone to edit, or undefined to add a new one. */
  milestone?: MilestoneDto;
  onClose: () => void;
}

export function MilestoneModal({ releaseId, milestone, onClose }: MilestoneModalProps) {
  const add = useAddMilestone(releaseId);
  const update = useUpdateMilestone(releaseId);
  const editing = Boolean(milestone?.milestoneId);
  const [date, setDate] = useState(milestone?.date ?? "");
  const [name, setName] = useState(milestone?.name ?? "");
  const [description, setDescription] = useState(milestone?.description ?? "");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    if (!date) {
      setError("Pick a date for the milestone.");
      return;
    }
    if (!name.trim()) {
      setError("Give the milestone a name.");
      return;
    }
    const body: MilestoneUpsertRequest = { date, name: name.trim(), description: description.trim() || null };
    const onSuccess = () => onClose();
    const onError = (err: Error) => setError(err.message);
    if (editing) update.mutate({ milestoneId: milestone!.milestoneId as string, body }, { onSuccess, onError });
    else add.mutate(body, { onSuccess, onError });
  }

  return (
    <Modal title={editing ? "Edit milestone" : "Add milestone"} onClose={onClose} dirty="auto">
      <div className={styles.form}>
        <Field label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Field label="Name" placeholder="e.g. Single drop" value={name} onChange={(e) => setName(e.target.value)} />
        <label className={styles.textareaLabel}>
          Note (optional)
          <textarea
            className={styles.textarea}
            placeholder="e.g. DSP release 12:00 AM"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <div className={styles.actions}>
          <DialogCancel disabled={add.isPending || update.isPending} />
          <Button onClick={submit} loading={add.isPending || update.isPending}>
            {editing ? "Save milestone" : "Add milestone"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
